import React, { useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { Menu, Zap, Wrench, Settings, Home, Shield, Database, Target, FileText, BarChart3 } from 'lucide-react';
import { canAccessIntegrations, canAccessDevs, canAccessCddi } from './firestoreFunctions';

const linkBase = 'px-3 py-2 text-sm font-medium rounded-md';
const linkActive = 'text-blue-600 bg-blue-50';
const linkInactive = 'text-gray-700 hover:bg-gray-100';

type Props = {
  user?: any;
  onLogout?: () => void;
  onToggleSidebar?: () => void; // Callback to toggle dashboard sidebar
  isDashboardPage?: boolean; // Whether we're on dashboard
};

export const TopNavSimple: React.FC<Props> = ({ user, onLogout, onToggleSidebar, isDashboardPage = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [navMenuOpen, setNavMenuOpen] = useState(false);
  const [toolsMenuOpen, setToolsMenuOpen] = useState(false);
  const [settingsMenuOpen, setSettingsMenuOpen] = useState(false);
  const [quickActionsMenuOpen, setQuickActionsMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [hasIntegrationAccess, setHasIntegrationAccess] = useState(false);
  const [hasDevAccess, setHasDevAccess] = useState(false);
  const [hasCddiAccess, setHasCddiAccess] = useState(false);

  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const navBtnRef = useRef<HTMLButtonElement | null>(null);
  const toolsBtnRef = useRef<HTMLButtonElement | null>(null);
  const settingsBtnRef = useRef<HTMLButtonElement | null>(null);
  const quickActionsBtnRef = useRef<HTMLButtonElement | null>(null);
  const userBtnRef = useRef<HTMLButtonElement | null>(null);

  // Navigation items for hamburger menu (only on non-dashboard pages)
  const navItems = useMemo(
    () => [
      { to: '/', label: 'Home', icon: Home },
    ],
    []
  );

  // Tools menu items (require integration access)
  const toolsItems = useMemo(() => {
    if (!hasIntegrationAccess) return [];
    const items = [{ to: '/integrations', label: 'Integrations', icon: Database }];
    if (hasCddiAccess) {
      items.push({ to: '/challenges', label: 'Challenges', icon: Target });
    }
    return items;
  }, [hasIntegrationAccess, hasCddiAccess]);

  // Settings/integration menu items (filtered by access)
  const settingsItems = useMemo(() => {
    const items = [{ to: '/admin', label: 'Admin', icon: Shield }];
    if (hasIntegrationAccess) {
      if (hasDevAccess && hasCddiAccess) {
        items.push({ to: '/cddi-demo', label: 'CDDI Demo', icon: FileText });
      }
      if (hasCddiAccess) {
        items.push(
          { to: '/cddi-batch', label: 'CDDI Batch', icon: FileText },
          { to: '/cddi-results', label: 'CDDI Results', icon: BarChart3 }
        );
      }
    }
    if (hasDevAccess) {
      items.push({ to: '/vote-analysis', label: 'Vote Analysis', icon: BarChart3 });
    }
    return items;
  }, [hasIntegrationAccess, hasDevAccess, hasCddiAccess]);

  // Quick actions
  const quickActions: { label: string; action: () => void }[] = [
  ];

  useEffect(() => {
    let cancelled = false;

    const evaluateAccess = async () => {
      const userId = user?.uid;
      if (!userId) {
        if (!cancelled) {
          setHasIntegrationAccess(false);
          setHasDevAccess(false);
        }
        return;
      }

      try {
        const [cddi, dev, cddiRole] = await Promise.all([
          canAccessIntegrations(userId),
          canAccessDevs(userId),
          canAccessCddi(userId),
        ]);
        if (!cancelled) {
          setHasIntegrationAccess(Boolean(cddi));
          setHasDevAccess(Boolean(dev));
          setHasCddiAccess(Boolean(cddiRole));
        }
      } catch (error) {
        if (!cancelled) {
          setHasIntegrationAccess(false);
          setHasDevAccess(false);
          setHasCddiAccess(false);
        }
      }
    };

    evaluateAccess();
    return () => {
      cancelled = true;
    };
  }, [user?.uid]);

  useEffect(() => {
    if (!toolsItems.length && toolsMenuOpen) {
      setToolsMenuOpen(false);
    }
  }, [toolsItems.length, toolsMenuOpen]);

  useEffect(() => {
    if (!settingsItems.length && settingsMenuOpen) {
      setSettingsMenuOpen(false);
    }
  }, [settingsItems.length, settingsMenuOpen]);

  // Close menus on outside click
  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node;

      if (navMenuOpen && !navBtnRef.current?.contains(target) && !(target as HTMLElement).closest('#nav-menu')) {
        setNavMenuOpen(false);
      }
      if (toolsMenuOpen && !toolsBtnRef.current?.contains(target) && !(target as HTMLElement).closest('#tools-menu')) {
        setToolsMenuOpen(false);
      }
      if (settingsMenuOpen && !settingsBtnRef.current?.contains(target) && !(target as HTMLElement).closest('#settings-menu')) {
        setSettingsMenuOpen(false);
      }
      if (quickActionsMenuOpen && !quickActionsBtnRef.current?.contains(target) && !(target as HTMLElement).closest('#quick-actions-menu')) {
        setQuickActionsMenuOpen(false);
      }
      if (userMenuOpen && !userBtnRef.current?.contains(target) && !(target as HTMLElement).closest('#user-menu')) {
        setUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [navMenuOpen, toolsMenuOpen, settingsMenuOpen, quickActionsMenuOpen, userMenuOpen]);

  // Expose top nav height as a CSS variable
  useEffect(() => {
    const setHeightVar = () => {
      const el = wrapperRef.current;
      if (!el) return;
      const h = Math.round(el.getBoundingClientRect().height || el.offsetHeight || 0);
      const clamped = Math.max(48, h);
      document.documentElement.style.setProperty('--topnav-height', `${clamped}px`);
      if (document.body) {
        document.body.style.setProperty('--topnav-height', `${clamped}px`);
      }
      const spacer = document.getElementById('topnav-spacer');
      if (spacer) spacer.style.height = `${clamped}px`;
    };

    setHeightVar();
    window.addEventListener('resize', setHeightVar);

    let ro: ResizeObserver | null = null;
    if ('ResizeObserver' in window && wrapperRef.current) {
      ro = new ResizeObserver(() => setHeightVar());
      ro.observe(wrapperRef.current);
    }

    return () => {
      window.removeEventListener('resize', setHeightVar);
      if (ro && wrapperRef.current) ro.disconnect();
    };
  }, []);

  const MenuItem = ({ to, label, icon: Icon, onClick }: any) => (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-gray-100 transition-colors ${
          isActive ? 'text-blue-600 bg-blue-50' : 'text-gray-700'
        }`
      }
      onClick={onClick}
    >
      <Icon className="w-4 h-4" />
      <span>{label}</span>
    </NavLink>
  );

  const ActionItem = ({ label, onClick }: any) => (
    <button
      onClick={() => {
        onClick();
        setQuickActionsMenuOpen(false);
      }}
      className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-100 transition-colors w-full text-left"
    >
      {label}
    </button>
  );

  return (
    <div ref={wrapperRef} className="bg-white border-b fixed top-0 left-0 right-0 z-[60]">
      <div className="w-full px-4 py-2 flex items-center justify-between gap-3">
        {/* Left: Logo + Hamburger */}
        <div className="flex items-center gap-3">
          <button
            ref={navBtnRef}
            onClick={() => {
              // Dispatch event to toggle sidebar (dashboard will prevent default if it handles it)
              const event = new CustomEvent('toggle-sidebar-request', { cancelable: true });
              const wasHandled = !window.dispatchEvent(event);

              // If not handled by dashboard (event not prevented), show navigation menu
              if (!wasHandled) {
                setNavMenuOpen(!navMenuOpen);
              }
            }}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            title="Menu"
          >
            <Menu className="w-5 h-5 text-gray-700" />
          </button>

          <NavLink to="/" className="flex items-center gap-2.5">
            <img src="/icon_dark.svg" alt="Interpretos" className="h-7 w-auto" />
            <span className="text-base font-semibold text-gray-900">Interpretos</span>
          </NavLink>
        </div>

        {/* Right: Action buttons + User */}
        <div className="flex items-center gap-1">
          {/* Quick Actions */}
          {quickActions.length > 0 && (
          <button
            ref={quickActionsBtnRef}
            onClick={() => setQuickActionsMenuOpen(!quickActionsMenuOpen)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            title="Quick Actions"
          >
            <Zap className="w-5 h-5 text-gray-700" />
          </button>
          )}

          {/* Tools */}
          {toolsItems.length > 0 && (
            <button
              ref={toolsBtnRef}
              onClick={() => setToolsMenuOpen(!toolsMenuOpen)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title="Tools"
            >
              <Wrench className="w-5 h-5 text-gray-700" />
            </button>
          )}

          {/* Settings */}
          {settingsItems.length > 0 && (
            <button
              ref={settingsBtnRef}
              onClick={() => setSettingsMenuOpen(!settingsMenuOpen)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title="Settings & CDDI"
            >
              <Settings className="w-5 h-5 text-gray-700" />
            </button>
          )}

          {/* User Avatar */}
          <button
            ref={userBtnRef}
            className="ml-1 h-8 w-8 rounded-full bg-blue-600 text-white text-xs font-semibold flex items-center justify-center select-none hover:bg-blue-700 transition-colors"
            onClick={() => setUserMenuOpen(!userMenuOpen)}
            title={user?.email || 'Account'}
          >
            {(() => {
              const name = user?.displayName || user?.email || 'U';
              const parts = String(name).split('@')[0].split(' ');
              const initials = parts.length > 1 ? `${parts[0][0]}${parts[parts.length - 1][0]}` : String(name).substring(0, 2);
              return initials.toUpperCase();
            })()}
          </button>
        </div>
      </div>

      {/* Navigation Menu */}
      {navMenuOpen && (
        <div id="nav-menu" className="absolute left-3 mt-1 bg-white border rounded-lg shadow-lg z-[70] min-w-[200px]">
          <div className="py-1">
            {navItems.map((item) => (
              <MenuItem
                key={item.to}
                to={item.to}
                label={item.label}
                icon={item.icon}
                onClick={() => setNavMenuOpen(false)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Quick Actions Menu */}
      {quickActionsMenuOpen && (
        <div id="quick-actions-menu" className="absolute right-32 mt-1 bg-white border rounded-lg shadow-lg z-[70] min-w-[180px]">
          <div className="py-1">
            {quickActions.map((action, idx) => (
              <ActionItem key={idx} label={action.label} onClick={action.action} />
            ))}
          </div>
        </div>
      )}

      {/* Tools Menu */}
      {toolsMenuOpen && toolsItems.length > 0 && (
        <div id="tools-menu" className="absolute right-24 mt-1 bg-white border rounded-lg shadow-lg z-[70] min-w-[200px]">
          <div className="py-1">
            {toolsItems.map((item) => (
              <MenuItem
                key={item.to}
                to={item.to}
                label={item.label}
                icon={item.icon}
                onClick={() => setToolsMenuOpen(false)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Settings Menu */}
      {settingsMenuOpen && settingsItems.length > 0 && (
        <div id="settings-menu" className="absolute right-16 mt-1 bg-white border rounded-lg shadow-lg z-[70] min-w-[200px]">
          <div className="py-1">
            {settingsItems.map((item) => (
              <MenuItem
                key={item.to}
                to={item.to}
                label={item.label}
                icon={item.icon}
                onClick={() => setSettingsMenuOpen(false)}
              />
            ))}
          </div>
        </div>
      )}

      {/* User Menu */}
      {userMenuOpen && (
        <div id="user-menu" className="absolute right-3 mt-1 bg-white border rounded-lg shadow-lg z-[70] min-w-[200px]">
          <div className="px-4 py-3 border-b">
            <div className="text-sm font-medium text-gray-900">{user?.displayName || 'Signed in'}</div>
            <div className="text-xs text-gray-500 truncate">{user?.email}</div>
          </div>
          <div className="py-1">
            <button
              className="w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-gray-100 transition-colors"
              onClick={() => {
                setUserMenuOpen(false);
                onLogout && onLogout();
              }}
            >
              Logout
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TopNavSimple;
